/**
 * App, Web, and Device Type Constants
 *
 * Copied from scio2: optisigns-portal-common/libs/common/constant/src/lib/app.constant.ts
 * These define all content types, web embed types, and device platforms.
 */

// --- App Types (100+ content/widget types) ---

export const APP_TYPES = {
  // Social Media
  TWITTER: 'twitter',
  INSTAGRAM: 'instagram',
  INSTAGRAM_COUNTER: 'instagramCounter',
  INSTAGRAM_OFFICE_COUNTER: 'InstagramOfficeCounter',
  INSTAGRAM_LOGIN: 'instagramBasic',
  INSTAGRAM_BUSINESS: 'instagramBusiness',
  FACEBOOK: 'facebook',
  FACEBOOK_COUNTER: 'facebookCounter',
  FACEBOOK_RECOMMENDATION: 'facebookRecommendation',
  FACEBOOK_STREAMING: 'facebookStreaming',
  FACEBOOK_VIDEO: 'facebookVideo',
  TWITTER_COUNTER: 'twitterCounter',
  LINKEDIN: 'linkedin',
  TWITCH: 'twitch',
  FOLLOW_US: 'followUs',
  SOCIAL_MIX: 'socialMix',

  // Weather & News
  WEATHER: 'weather',
  NEWS: 'news',
  RSS: 'rss',
  CNN: 'cnn',
  ESPN: 'espn',
  TECHCRUNCH: 'techcrunch',
  THE_GUARDIAN: 'theGuardian',
  NY_TIMES: 'nyTimes',
  WSJ: 'wsj',
  CBC: 'cbc',
  ON_THIS_DAY: 'onThisDay',
  COVID19: 'covid19',

  // Reviews
  REVIEW: 'review',
  YELP_REVIEWS: 'yelp',

  // Time & Calendar
  SIMPLE_CALENDAR: 'simplecalendar',
  SIMPLE_CLOCK: 'simpleClock',
  WORLD_CLOCK: 'worldClock',
  COUNT_DOWN: 'countDown',
  COUNT_UP: 'countUp',
  GOOGLE_CALENDAR: 'googleCalendar',
  MICROSOFT_CALENDAR: 'microsoftCalendar',
  MEETING_CALENDAR: 'meetingCalendar',
  CALENDAR_MIX: 'calendarMix',

  // Financial
  STOCK: 'stock',
  WORLD_MARKET_INDEX: 'worldMarketIndex',
  US_MARKET_INDEX: 'usMarketIndex',
  CRYPTO_INDEX: 'cryptoIndex',
  MARKET_INDEX: 'marketIndex',
  CRYPTO_EXCHANGE: 'cryptoExchange',
  CURRENCY_EXCHANGE: 'currencyExchange',

  // Google Suite
  GOOGLE_TRAFFIC: 'googleTrafic',
  GOOGLE_DRIVE: 'googleDrive',
  GOOGLE_DOCS: 'googleDocs',
  GOOGLE_SLIDES: 'googleSlides',
  GOOGLE_SHEETS: 'googleSheets',
  GOOGLE_SHEETS_DATA_SOURCE: 'googleSheetsDataSource',

  // Microsoft Suite
  POWERPOINT: 'powerpoint',
  MS_EXCEL: 'microsoftExcel',
  MS_EXCEL_DATA_SOURCE: 'microsoftExcelDataSource',
  SHAREPOINT: 'sharepoint',
  MS_TEAM: 'msTeam',
  MS_TEAMS_CHANNEL: 'msteamsChannel',
  ONEDRIVE: 'onedrive',

  // Meetings
  ZOOM_MEETING: 'zoomMeeting',
  MS_MEETING: 'MSMeeting',
  MS_MEETING_LIVE: 'MSMeetingLive',

  // Communication
  SLACK: 'slack',
  YAMMER: 'yammer',
  YAMMER_LOGIN: 'yammerLogin',
  NOTICE_BOARD: 'noticeBoard',
  MOTIVATIONAL_QUOTES: 'motivationalQuotes',

  // Design & Canvas
  CANVAS: 'canvas',
  CANVA: 'canva',
  CANVA_EXPORT: 'canvaExport',

  // Data & Tables
  DATA_SOURCE: 'datasource',
  TABLE_TEMPLATE: 'tableTemplate',
  TABLE: 'table',
  SMARTSHEET: 'smartsheet',

  // Kiosk
  INTERACTIVE_KIOSK: 'interactiveKiosk',
  KIOSK_DESIGNER: 'kioskDesigner',
  ADVANCED_KIOSK: 'advancedKiosk',

  // Streaming & Media
  LIVE_TV: 'liveTv',
  LIVE_STREAM: 'livestream',
  SCREEN_SHARE: 'screenShare',
  MRSS: 'mrss',
  FEED: 'feed',
  BACKGROUND_MUSIC: 'backgroundMusic',

  // Storage
  DROPBOX: 'dropbox',
  LOCAL_DRIVE: 'localDrive',

  // OptiSigns Special
  EMERGENCY_MESSAGE: 'emergencyMessage',
  FLASH_MESSAGE: 'flashMessage',
  EMERGENCY_FEED: 'emergencyFeed',
  AERICAST: 'AeriCast',
  VIRTUAL_SCREEN: 'virtualScreen',
  AI: 'ai',
  SENSOR: 'sensor',
  CONTENT_ROTATION: 'contentRotation',
  CAP_ALERT: 'CAPAlert',

  // OptiDev
  OPTIDEV: 'optidev',

  // Web & Advanced
  SCROLLING_WEB: 'scrollingWeb',
  ADVANCED_WEBVIEW: 'advancedWebView',
  SCREENSHOT_WEBSITE: 'screenshotWebsite',
  SCREEN_CAPTURE_APP: 'screenCaptureApp',
  SCRIPTING_ABILITY: 'scriptingAbility',
  ADS_APP: 'adsApp',
} as const;

export type AppType = (typeof APP_TYPES)[keyof typeof APP_TYPES];

// --- Common Types (special app types with additional handling) ---

export const COMMON_TYPE = {
  QR: 'qr',
  EMERGENCY_MESSAGE: 'emergencyMessage',
  FLASH_MESSAGE: 'flashMessage',
  EMERGENCY_FEED: 'emergencyFeed',
  LIVE_TV_ADS: 'liveTvAds',
  SMALLAPP: 'microservice',
  SMALLAPP_EXTEND: 'microserviceExtend',
  SCREEN_CAPTURE_APP: 'screenCaptureApp',
  GOOGLE_SLIDES: 'googleSlides',
  GOOGLE_SHEETS: 'googleSheets',
  GOOGLE_DOCS: 'googleDocs',
  POWERPOINT: 'powerpoint',
  MS_EXCEL: 'microsoftExcel',
  AERICAST: 'AeriCast',
  MS_EXCEL_DATA_SOURCE: 'microsoftExcelDataSource',
  QR_OVERLAY: 'qrOverlay',
  QR_OVERLAY_FILE: 'qrOverlayFile',
  VIDEO_WALL: 'videoWall',
} as const;

export type CommonType = (typeof COMMON_TYPE)[keyof typeof COMMON_TYPE];

// --- Sub Types ---

export const SUB_TYPES = {
  APP: 'app',
  STATIC: 'static',
  SCROLLING_STRIP: 'scrollingStrip',
  CALL_TO_ACTION: 'callToAction',
} as const;

export type SubType = (typeof SUB_TYPES)[keyof typeof SUB_TYPES];

// --- Web Types (embeddable web content) ---

export const WEB_TYPES = {
  // Video
  YOUTUBE: 'youtube',
  YOUTUBE_LIVE: 'youtubeLive',
  VIDEO: 'video',
  VIMEO: 'vimeo',

  // Business Intelligence
  GOOGLE_DATA_STUDIO: 'google-data-studio',
  GOOGLE_SLIDES: 'google-slides',
  MICROSOFT_BI: 'microsoft-bi',
  TABLEAU: 'tableau',
  GOOGLE_ANALYTICS: 'google-analytics',
  GOOGLE_ADS_DASHBOARD: 'google-aptiads',
  YOUTUBE_DASHBOARD: 'youtube-dashboard',
  GOOGLE_SEARCH_CONSOLE: 'google-search-console',
  GOOGLE_TRENDS: 'googleTrends',
  GOOGLE_SHEETS: 'googleSheets',

  // Productivity
  TRELLO: 'trello',
  JIRA: 'jira',
  ASANA: 'asana',
  ZENDESK: 'zendesk',
  NOTION: 'notion',

  // Calendar
  OUTLOOK_CAL: 'outlook-cal',
  GOOGLE_CAL: 'google-cal',

  // Social Walls
  WALLS_IO: 'walls-io',
  TAGGBOX: 'taggbox',

  // Meetings
  MS_MEETING: 'MSMeeting',
  MS_MEETING_LIVE: 'MSMeetingLive',

  // Web
  WEBSITE: 'website',
  EMBED: 'embed',
  STREAMING_APP: 'streamingApp',
  MINDMAP: 'mindmap',
  MATTERPORT: 'matterport',
  WEATHER_RADAR: 'weatherRadar',
} as const;

export type WebType = (typeof WEB_TYPES)[keyof typeof WEB_TYPES];

// --- Device Types (player platforms) ---

export const DEVICE_TYPES = {
  ANDROID: 'android',
  AMAZON: 'amazon',
  WINDOWS: 'windows',
  MACOS: 'mac-os',
  LINUX: 'linux',
  RASPBERRY_PI: 'raspberry-pi',
  BRIGHTSIGN: 'brightsign',
  WEBOS: 'web-os',
  SAMSUNG_SSSP: 'SamsungSSSP',
  CHROME_OS: 'chrome-os',
  ARM_LINUX: 'ARMLinux',
  SAMSUNG_TV: 'SamsungTV',
  APPLE_TV: 'apple-tv',
  IOS: 'iOS',
  IPADOS: 'iPadOS',
  ROKU: 'roku',
  WEBPLAYER: 'chrome',
  WEBOS_SIGNAGE: 'webos-signage',
  WEBOS_SIGNAGE_ATHENA: 'webos-signage-athena',
  EMBEDDED: 'embedded',
  ELECTRON: 'electron',
  MAC: 'mac',
  UNKNOWN: 'optisigns',
} as const;

export type DeviceType = (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];

// --- Device OS ---

export const DEVICE_OS = {
  ANDROID: 'android',
  IPADOS: 'iPadOS',
  TVOS: 'tvOS',
  IOS: 'iOS',
  TIZEN: 'tizen',
  BRIGHTSIGN: 'brightsign',
  WEBOS: 'webos',
  WINDOWS: 'windows',
  LINUX: 'linux',
  MAC: 'mac',
  CHROME: 'chrome',
  WIN_32: 'win32',
} as const;

export type DeviceOs = (typeof DEVICE_OS)[keyof typeof DEVICE_OS];

// --- Device Model & Manufacturer ---

export const DEVICE_MODEL = {
  OPTISIGNS_PRO: 'optisigns_pro',
} as const;

export type DeviceModel = (typeof DEVICE_MODEL)[keyof typeof DEVICE_MODEL];

export const DEVICE_MANUFACTURER = {
  OPTISIGNS: 'optisigns',
} as const;

export type DeviceManufacturer = (typeof DEVICE_MANUFACTURER)[keyof typeof DEVICE_MANUFACTURER];

// --- App Type Restrictions (which apps can be used where) ---

export const APP_REJECT_ASSIGN = [
  APP_TYPES.VIRTUAL_SCREEN,
  APP_TYPES.AI,
  APP_TYPES.GOOGLE_SHEETS_DATA_SOURCE,
  APP_TYPES.CAP_ALERT,
  APP_TYPES.SENSOR,
  APP_TYPES.MS_EXCEL_DATA_SOURCE,
] as const;

export const PLAYLIST_IGNORE_APP_TYPES = [APP_TYPES.CONTENT_ROTATION, APP_TYPES.CAP_ALERT] as const;

/** App types whose duration cannot be changed in bulk edit — scio1 ASSET_APPTYPE_PLAYLIST_SPEACIAL */
export const PLAYLIST_SPECIAL_APP_TYPES = [
  APP_TYPES.GOOGLE_DRIVE,
  APP_TYPES.ONEDRIVE,
  APP_TYPES.DROPBOX,
  APP_TYPES.CONTENT_ROTATION,
  APP_TYPES.MRSS,
] as const;

/** App types that behave as cloud storage folders (DRIVE mode) — scio1 ASSET_APP_TYPE_ASSET_DETAIL_SPECIAL */
export const DRIVE_APP_TYPES = [
  APP_TYPES.GOOGLE_DRIVE,
  APP_TYPES.ONEDRIVE,
  APP_TYPES.DROPBOX,
] as const;

export function isDriveAppType(appType?: string): boolean {
  return DRIVE_APP_TYPES.includes(appType as (typeof DRIVE_APP_TYPES)[number]);
}

export const APP_COMMON_TYPES_REJECT_SCHEDULE = [
  COMMON_TYPE.QR,
  APP_TYPES.EMERGENCY_MESSAGE,
  APP_TYPES.EMERGENCY_FEED,
  APP_TYPES.FLASH_MESSAGE,
  APP_TYPES.CONTENT_ROTATION,
] as const;

export const APP_COMMON_TYPES_REJECT_DEVICE = [
  COMMON_TYPE.QR,
  APP_TYPES.EMERGENCY_MESSAGE,
  APP_TYPES.EMERGENCY_FEED,
  APP_TYPES.FLASH_MESSAGE,
] as const;

// --- Add-On Types (bulk assignment via toolbar) ---

export const APP_TYPE_ADD_ONS = {
  BACKGROUND_MUSIC: APP_TYPES.BACKGROUND_MUSIC,
  AERICAST: COMMON_TYPE.AERICAST,
  VIDEO_WALL: COMMON_TYPE.VIDEO_WALL,
} as const;

export type AppTypeAddOn = (typeof APP_TYPE_ADD_ONS)[keyof typeof APP_TYPE_ADD_ONS];
